import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Cta } from "@/components/site/ui";
import { resetPassword } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { IMAGES } from "@/data/content";

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return "Something went wrong. Please try again.";
  return typeof d === "string" ? d : (Array.isArray(d) ? d.map((x) => x?.msg || "").join(" ") : String(d));
};

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) setErr("Missing reset token. Please request a new link.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) return setErr("Password must be at least 6 characters.");
    if (pw !== pw2) return setErr("Passwords don't match.");
    setBusy(true);
    try {
      const u = await resetPassword(token, pw);
      setUser(u);
      toast.success("Password updated. You're signed in.");
      navigate(u.role === "admin" ? "/admin" : "/account", { replace: true });
    } catch (e2) {
      setErr(errText(e2));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "bg-white/5 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-white/40 h-12 rounded-xl";

  return (
    <section className="relative min-h-screen bg-[#1C1A17] text-white overflow-hidden">
      <img src={IMAGES.heroLiving} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
      <div className="absolute inset-0 bg-[#1C1A17]/70" />
      <div className="relative z-10 min-h-screen flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md rounded-3xl bg-white/[0.04] backdrop-blur-xl border border-white/10 p-8 sm:p-10">
          <Link to="/" className="font-head text-xl font-extrabold tracking-tight">
            SPHERIO LIVING<span className="text-[#1C1A17]">.</span>
          </Link>
          <h1 className="font-head text-3xl font-medium mt-8">Set a new password</h1>
          <p className="text-white/55 text-sm mt-2">
            Choose a strong password. You’ll be signed in automatically once it’s saved.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
            {err && (
              <div data-testid="reset-error" className="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                {err}
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-white/70">New password</Label>
              <Input data-testid="reset-password-1" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Confirm password</Label>
              <Input data-testid="reset-password-2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="••••••••" className={inputCls} />
            </div>
            <Cta type="submit" onClick={() => {}} data-testid="reset-submit" variant="light" className="w-full justify-center" disabled={!token}>
              {busy ? "Saving..." : "Save new password"}
            </Cta>
            <div className="text-center text-sm text-white/50">
              <Link to="/login" className="text-white hover:text-[#1C1A17]">← Back to sign in</Link>
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-white/10 text-center">
            <Link to="/" className="text-white/50 hover:text-white text-sm">← Back to site</Link>
          </div>
        </div>
      </div>
      <Toaster position="top-center" richColors />
    </section>
  );
}
